/**
 * Budget / scope of work (BudgetExtract — eastview-os app/models/extraction_schemas.py).
 * Flat payload: propertyAddress, documentType, preparedBy, lineItems[],
 * statedTotalBudget, hardCosts, softCosts, contingency, contingencyPercent,
 * commentary, scopeQualityAssessment.
 */
import { fmtMoney, fmtPercent, addressMissing, isAddressLike, toNumber } from "../format/registry";

const cost = (li) => toNumber(li?.estimatedTotalLineItemCost);
const isSoft = (li) => /soft/i.test(String(li?.costCategory || ""));
const isContingency = (li) => /contingen/i.test(String(li?.itemCategory || ""));
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const derive = (p) => {
  const items = Array.isArray(p?.lineItems) ? p.lineItems : [];
  const sum = items.reduce((n, li) => n + (cost(li) || 0), 0);
  const soft = items.filter(isSoft).reduce((n, li) => n + (cost(li) || 0), 0);
  const contingency = items.filter(isContingency).reduce((n, li) => n + (cost(li) || 0), 0);
  const hard = sum - soft - contingency;
  return {
    sum,
    soft,
    contingency,
    hard,
    scope: sum - contingency,
    unpriced: items.filter((li) => cost(li) === null).length,
    missingAddress: isAddressLike(p?.propertyAddress) ? addressMissing(p.propertyAddress) : null,
  };
};

const budgetConfig = {
  id: "budget",
  schemas: ["BudgetExtract"],
  routes: ["budget"],
  title: "Budget",
  derive,
  detailPaths: ["_docType", "_extractionMethod"],
  sections: [
    {
      kind: "fields",
      id: "overview",
      title: "Overview",
      fields: [
        {
          path: "documentType",
          label: "Document type",
          note: (v, p) => (p?._docType ? { text: p._docType, tone: "muted" } : undefined),
        },
        { path: "preparedBy", label: "Prepared by" },
        {
          path: "propertyAddress",
          label: "Property",
          note: (v, p, d) => (d.missingAddress ? { text: `${cap(d.missingAddress)} not provided`, tone: "warn" } : null),
        },
        {
          path: "statedTotalBudget",
          label: "Stated total",
          format: "money",
          breakdown: (p, d) =>
            [
              { label: "Hard", value: d.hard, tone: "primary" },
              { label: "Soft", value: d.soft, tone: "secondary" },
              { label: "Contingency", value: d.contingency, tone: "tertiary" },
            ].filter((b) => b.value > 0),
        },
        {
          path: "hardCosts",
          label: "Hard costs",
          format: "money",
          note: (v, p, d) => {
            const n = toNumber(v);
            if (n === null || Math.abs(n - d.hard) < 0.5) return undefined;
            const diff = n - d.hard;
            return diff > 0
              ? { text: `Includes ${fmtMoney(diff)} in soft costs`, tone: "warn" }
              : { text: `Line items put hard costs at ${fmtMoney(d.hard)}`, tone: "warn" };
          },
        },
        { path: "softCosts", label: "Soft costs", format: "money" },
        {
          path: "contingency",
          label: "Contingency",
          format: "money",
          note: (v, p, d) => {
            const n = toNumber(v) ?? d.contingency;
            return d.scope > 0 && n ? { text: `${fmtPercent(n / d.scope)} of work scope`, tone: "muted" } : undefined;
          },
        },
        { path: "contingencyPercent", label: "Contingency %", format: "percent", hideWhenEmpty: true },
      ],
    },
    {
      kind: "table",
      id: "line-items",
      title: "Line items",
      path: "lineItems",
      titleKey: "itemName",
      subtitleKey: "description",
      valueKey: "estimatedTotalLineItemCost",
      valueLabel: "Cost",
      emptyValueLabel: "No cost",
      groupBy: "itemCategory",
      groupNoun: "category",
      emptyGroupLabel: "Listed without a cost",
      columns: [],
      flagRowsWithoutValue: true,
      expectedTotal: (p) => p?.statedTotalBudget,
      meta: (withValue, withoutValue) =>
        withoutValue ? `${withValue} priced, ${withoutValue} without a cost` : `${withValue} priced`,
      tags: (row) => {
        const tags = [];
        const sq = toNumber(row.squareFootageRequired);
        const qty = toNumber(row.quantityRequired);
        if (sq) tags.push({ label: `${sq.toLocaleString("en-US")} sq ft`, tone: "neutral" });
        else if (qty) tags.push({ label: `Qty ${qty.toLocaleString("en-US")}${row.unit ? ` ${row.unit}` : ""}`, tone: "neutral" });
        if (isSoft(row)) tags.push({ label: "Soft cost", tone: "purple" });
        if (isContingency(row)) tags.push({ label: "Contingency", tone: "accent" });
        if (row.stage === "finish") tags.push({ label: "Finish only", tone: "neutral" });
        if (row.stage === "rough") tags.push({ label: "Rough only", tone: "neutral" });
        return tags;
      },
    },
    {
      kind: "findings",
      id: "scope-assessment",
      title: "Scope assessment",
      path: "scopeQualityAssessment",
      summaryPath: "commentary",
      refsFromTable: "line-items",
    },
  ],
  disableBuiltins: ["address-incomplete", "total-mismatch"],
  checks: (p, d) => {
    const out = [];
    const stated = toNumber(p?.statedTotalBudget);
    if (stated !== null && d.sum > 0) {
      out.push(
        Math.abs(stated - d.sum) < 0.5
          ? {
              id: "budget-reconcile",
              tone: "ok",
              title: `Line items add up to the stated total of ${fmtMoney(stated)}`,
              target: { sectionId: "line-items" },
            }
          : {
              id: "budget-reconcile",
              tone: "crit",
              title: `Line items total ${fmtMoney(d.sum)}, stated total is ${fmtMoney(stated)}`,
              detail: `Off by ${fmtMoney(Math.abs(stated - d.sum))}.`,
              target: { sectionId: "line-items" },
            },
      );
    }
    const hardCosts = toNumber(p?.hardCosts);
    if (hardCosts !== null && hardCosts - d.hard >= 0.5 && d.soft > 0) {
      out.push({
        id: "budget-hard-soft",
        tone: "warn",
        title: `Hard costs of ${fmtMoney(hardCosts)} include ${fmtMoney(hardCosts - d.hard)} in soft costs`,
        detail: `Hard costs alone are ${fmtMoney(d.hard)}.`,
        target: { sectionId: "overview", path: "hardCosts" },
      });
    }
    if (d.unpriced) {
      out.push({
        id: "budget-unpriced",
        tone: "warn",
        title: `${d.unpriced} line ${d.unpriced === 1 ? "item has" : "items have"} no cost`,
        detail: "Price them or list them under exclusions before the first draw.",
        target: { sectionId: "line-items", expandGroup: "lineItems::__empty" },
      });
    }
    if (d.missingAddress) {
      out.push({
        id: "budget-address",
        tone: "warn",
        title: `Property address is missing ${d.missingAddress}`,
        target: { sectionId: "overview", path: "propertyAddress" },
      });
    }
    const cont = toNumber(p?.contingency) ?? d.contingency;
    if (cont && d.scope > 0) {
      const ratio = cont / d.scope;
      out.push({
        id: "budget-contingency",
        tone: ratio <= 0.1 ? "ok" : "warn",
        title: `Contingency is ${fmtPercent(ratio)} of the work scope`,
        detail: ratio <= 0.1 ? "Within the 10% guideline." : "Above the 10% guideline.",
        target: { sectionId: "overview", path: "contingency" },
      });
    }
    return out;
  },
};

export default budgetConfig;
